import React, { useCallback, useEffect, useRef, useState } from 'react';
import { userAppData } from '../MainApp.js';
import './OncallDesktop.css';
import './OncallMobile.css';
import LoadingComp from '../common/loading.js';
import { ApiError } from '../common/apiError.js';
import { SEC_ApiCall, sortArrayByNumericalProperty, sortArrayByProperty } from '../func.js';
import { OnCallActiveCall } from './OnCallActiveCall.js';
import { OnCallPendingCall } from './OnCallPendingCall.js';
import { MdNotificationAdd } from 'react-icons/md';
import { FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import { OnCallAddNewCallFormModal } from '../common/modals.js';
import RotaAddCoverLauncher from '../common/RotaAddCoverLauncher.js';
import { toast } from 'react-toastify';
import Header from '../common/header.js';




const ONCALL_PENDING_AUDIO_URL = 'https://schoolengine-schoollogos.s3.eu-west-2.amazonaws.com/onCallPendingCall.mp3';
const ONCALL_ROTA_ERROR_TEXT = 'Error loading rota';
const ONCALL_ROTA_NO_PERSON_TEXT = 'No Person Found';

function timeStringToMinutes(value) {
    const parts = String(value || '').split(':');
    if (parts.length !== 2) {
        return Number.NaN;
    }
    return (Number(parts[0]) * 60) + Number(parts[1]);
}

function isCurrentOnCallBlock(block, now = new Date()) {
    const startMinutes = timeStringToMinutes(block?.start);
    const endMinutes = timeStringToMinutes(block?.end);
    const currentMinutes = (now.getHours() * 60) + now.getMinutes();

    if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
        return false;
    }

    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
}

function getCurrentOnCallDisplayName(blocks, now = new Date()) {
    const currentMinutes = (now.getHours() * 60) + now.getMinutes();
    const matchingBlock = (Array.isArray(blocks) ? blocks : []).find((block) => {
        const startMinutes = timeStringToMinutes(block.start);
        const endMinutes = timeStringToMinutes(block.end);
        if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
            return false;
        }
        return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    });

    if (!matchingBlock) {
        return ONCALL_ROTA_NO_PERSON_TEXT;
    }

    const displayName = String(matchingBlock.displayName || '').trim();
    if (!displayName || displayName.toUpperCase() === 'NONE') {
        return ONCALL_ROTA_NO_PERSON_TEXT;
    }

    return displayName;
}

function getCurrentOnCallOverrideState(blocks, now = new Date()) {
    const currentMinutes = (now.getHours() * 60) + now.getMinutes();
    const matchingBlock = (Array.isArray(blocks) ? blocks : []).find((block) => {
        const startMinutes = timeStringToMinutes(block?.start);
        const endMinutes = timeStringToMinutes(block?.end);
        if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
            return false;
        }
        return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    });

    return Boolean(matchingBlock?.isOverride);
}

function OnCallDayRota({ blocks = [] }) {
    const safeBlocks = Array.isArray(blocks) ? blocks : [];

    if (safeBlocks.length === 0) {
        return (
            <div className='oncall_day_rota_empty'>
                No rota blocks are available for today.
            </div>
        );
    }

    return (
        <div className='oncall_day_rota_strip'>
            {safeBlocks.map((block) => {
                const displayName = String(block?.displayName || ONCALL_ROTA_NO_PERSON_TEXT).trim() || ONCALL_ROTA_NO_PERSON_TEXT;
                const activeClass = isCurrentOnCallBlock(block) ? ' oncall_day_rota_block--active' : '';
                return (
                    <div className={`oncall_day_rota_block${activeClass}`} key={block?.blockId || `${block?.start}-${block?.end}`}>
                        <span className='oncall_day_rota_block_period'>{block?.period || 'Block'}</span>
                        <span className='oncall_day_rota_block_time'>{block?.start} - {block?.end}</span>
                        <strong className='oncall_day_rota_block_name'>{displayName}</strong>
                        {block?.isOverride && block?.coveringDisplayName && (
                            <span className='oncall_day_rota_block_covering'>Covering: {block.coveringDisplayName}</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function OncallHome() {
    const allowManageCalls = Boolean(userAppData['onCall']?.allowManageCalls);
    const rotaAccessEntries = Array.isArray(userAppData?.rotasData?.access) ? userAppData.rotasData.access : [];
    const [callTypesState, setCallTypesState] = useState([]);

    const [pendingCallCountState, setPendingCallCountState] = useState(null);
    const [activeCallCountState, setActiveCallCountState] = useState(null);
    // states for active and pending calls
    const [activeCallsState, setActiveCallsState] = useState([]);
    const [pendingCallsState, setPendingCallsState] = useState([]);

    const [dataSltState, setDataSltState] = useState([]);
    const [isLoadedState, setIsLoadedState] = useState(false);
    const [apiErrorState, setApiErrorState] = useState(false);
    const [onCallRotaBlocksState, setOnCallRotaBlocksState] = useState([]);
    const [onCallRotaDisplayState, setOnCallRotaDisplayState] = useState(ONCALL_ROTA_NO_PERSON_TEXT);
    const [onCallRotaErrorState, setOnCallRotaErrorState] = useState(false);
    const [onCallCurrentOverrideState, setOnCallCurrentOverrideState] = useState(false);
    const [onCallRotaMetaState, setOnCallRotaMetaState] = useState({ masterRotaId: '', rotaName: '' });
    // modal appearence state
    const [modalActiveState, setModalActiveState] = useState(false);

    // Audio alert state. Browser policies require a user interaction before autoplay is allowed.
    const [audioEnabledState, setAudioEnabledState] = useState(localStorage.getItem('oncallAudioEnabled') === 'true');
    const [playNotificationAudioState, setPlayNotificationAudioState] = useState(false);
    const audioRef = useRef(null);
    const replayTimerRef = useRef(null);
    const hasLoadedCallsRef = useRef(false);
    const previousPendingCallIdsRef = useRef(new Set());
    const shouldShowOnCallDayRota = allowManageCalls && !onCallRotaErrorState && onCallRotaBlocksState.length > 0;

    const loadCurrentOnCallRota = useCallback(async () => {
        try {
            const response = await SEC_ApiCall('oncall/rota/current', {});
            const rotaBlocks = Array.isArray(response?.blocks) ? response.blocks : [];
            setOnCallRotaBlocksState(rotaBlocks);
            setOnCallRotaDisplayState(getCurrentOnCallDisplayName(rotaBlocks, new Date()));
            setOnCallRotaErrorState(false);
            setOnCallCurrentOverrideState(Boolean(response?.currentBlockIsOverride) || getCurrentOnCallOverrideState(rotaBlocks, new Date()));
            setOnCallRotaMetaState({
                masterRotaId: String(response?.masterRotaId || '').trim(),
                rotaName: String(response?.rotaName || '').trim(),
            });
        } catch (error) {
            console.error('Failed to load current OnCall rota', error);
            setOnCallRotaBlocksState([]);
            setOnCallRotaDisplayState(ONCALL_ROTA_ERROR_TEXT);
            setOnCallRotaErrorState(true);
            setOnCallCurrentOverrideState(false);
            setOnCallRotaMetaState({ masterRotaId: '', rotaName: '' });
        }
    }, []);

    function coerceBool(value) {
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'number') {
            return value !== 0;
        }

        const normalisedValue = String(value || '').trim().toLowerCase();
        return ['true', '1', 'yes', 'y', 'on'].includes(normalisedValue);
    }

    function userCanAddCover(masterRotaId) {
        const cleanMasterRotaId = String(masterRotaId || '').trim();
        if (!cleanMasterRotaId) {
            return false;
        }

        const matchingEntry = rotaAccessEntries.find((entry) => (
            entry
            && typeof entry === 'object'
            && String(entry.MasterRotaId || '').trim() === cleanMasterRotaId
        ));

        return coerceBool(matchingEntry?.allowOverride);
    }

    const apiCall = useCallback(async (params = {}) => { // check that all this data is needed for every user? Seems like the slt isnt needed for standard users
        var url = 'oncall';
        try {
            const response = await SEC_ApiCall(url, params, setApiErrorState);
            if (response["slt"]) {
                let sortedSlt = sortArrayByProperty(response['slt'], 'displayName', 'ascending');
                setDataSltState(sortedSlt);
            };
            setCallTypesState(response["callTypes"]);

            // loop through calls and add each one to correct states
            // Separate the calls into active and pending based on `isAcknowledged`
            const activeCalls = response["data"].filter(call => call.isAcknowledged);
            const pendingCalls = response["data"].filter(call => !call.isAcknowledged);
 
            // sort arrays into numerical order by submissionEpoch
            const activeCallsSorted = sortArrayByNumericalProperty(activeCalls, 'submissionEpoch', 'decending');
            const pendingCallsSorted = sortArrayByNumericalProperty(pendingCalls, 'submissionEpoch', 'decending');
            // Update state
            setActiveCallsState(activeCallsSorted);
            setPendingCallsState(pendingCallsSorted);

            setIsLoadedState(true);
            const pendingCallCount = pendingCalls.length;
            setPendingCallCountState(pendingCallCount);
            setActiveCallCountState(activeCalls.length);

            // Determine if this poll introduced at least one new pending call.
            const hasNewPendingCall = hasLoadedCallsRef.current && pendingCalls.some((call) => !previousPendingCallIdsRef.current.has(call.id));
            setPlayNotificationAudioState((previousState) => {
                if (!allowManageCalls || pendingCallCount === 0) {
                    return false;
                }
                if (hasNewPendingCall) {
                    return true;
                }
                return previousState;
            });

            previousPendingCallIdsRef.current = new Set(pendingCalls.map((call) => call.id));
            hasLoadedCallsRef.current = true;
        } catch (e) {
            console.log(e);
            setIsLoadedState(true);
            setApiErrorState(true);
        }
    }, [allowManageCalls]);

    const handlePendingCallAccepted = useCallback((acceptedCall) => {
        // Remove the call from pending immediately, then refresh in the background so the server-enriched active call is reloaded.
        const acceptedCallId = acceptedCall?.id;
        if (!acceptedCallId) {
            apiCall({}).catch((error) => {
                console.error('Failed to refresh OnCall calls after accepting pending call', error);
            });
            return;
        }

        setPendingCallsState((currentPendingCalls) => {
            const nextPendingCalls = currentPendingCalls.filter((call) => call.id !== acceptedCallId);
            setPendingCallCountState(nextPendingCalls.length);
            if (nextPendingCalls.length === 0) {
                setPlayNotificationAudioState(false);
            }
            return nextPendingCalls;
        });

        apiCall({}).catch((error) => {
            console.error('Failed to refresh OnCall calls after accepting pending call', error);
        });
    }, [apiCall]);


    useEffect(() => {
        // Initial load and continuous polling to detect new calls.
        const params = {};
        apiCall(params);
        loadCurrentOnCallRota();
        const apiTimer = setInterval(() => {
            apiCall(params);
        }, 30000);

        return () => {
            clearInterval(apiTimer);
        };
    }, [apiCall, loadCurrentOnCallRota]);

    useEffect(() => {
        if (onCallRotaErrorState) {
            setOnCallRotaDisplayState(ONCALL_ROTA_ERROR_TEXT);
            return undefined;
        }

        setOnCallRotaDisplayState(getCurrentOnCallDisplayName(onCallRotaBlocksState, new Date()));
        setOnCallCurrentOverrideState(getCurrentOnCallOverrideState(onCallRotaBlocksState, new Date()));
        const rotaTimer = setInterval(() => {
            setOnCallRotaDisplayState(getCurrentOnCallDisplayName(onCallRotaBlocksState, new Date()));
            setOnCallCurrentOverrideState(getCurrentOnCallOverrideState(onCallRotaBlocksState, new Date()));
        }, 30000);

        return () => {
            clearInterval(rotaTimer);
        };
    }, [onCallRotaBlocksState, onCallRotaErrorState]);

    useEffect(() => {
        // Play the alert tone on a loop while unacknowledged calls remain.
        const audio = audioRef.current;
        if (!audio) {
            return undefined;
        }

        const stopAudio = () => {
            if (replayTimerRef.current) {
                clearTimeout(replayTimerRef.current);
                replayTimerRef.current = null;
            }
            audio.pause();
            audio.currentTime = 0;
        };

        const playAudio = async () => {
            audio.currentTime = 0;
            try {
                await audio.play();
            } catch (error) {
                console.error('Error playing OnCall alert audio:', error);
                stopAudio();
                setPlayNotificationAudioState(false);
                toast.error('❌ Browser blocked audio playback. Click Enable Audio Alerts again.', { position: 'top-right', autoClose: 4000 });
            }
        };

        const handleAudioEnd = () => {
            const timeLeft = Math.max(0, 5000 - (audio.duration * 1000));
            replayTimerRef.current = setTimeout(() => {
                playAudio();
            }, timeLeft);
        };

        if (playNotificationAudioState && audioEnabledState) {
            playAudio();
            audio.addEventListener('ended', handleAudioEnd);
        } else {
            stopAudio();
        }

        return () => {
            audio.removeEventListener('ended', handleAudioEnd);
            if (!playNotificationAudioState || !audioEnabledState) {
                stopAudio();
            }
        };
    }, [playNotificationAudioState, audioEnabledState]);

    async function enableAudioAlerts() {
        const audio = audioRef.current;
        if (!audio) {
            toast.error('❌ Audio player is not ready yet. Please try again.', { position: 'top-right', autoClose: 4000 });
            return;
        }

        try {
            // Trigger a user-gesture-approved play/pause sequence to unlock future playback.
            audio.currentTime = 0;
            await audio.play();
            audio.pause();
            audio.currentTime = 0;
            setAudioEnabledState(true);
            localStorage.setItem('oncallAudioEnabled', 'true');
            if (pendingCallCountState > 0) {
                setPlayNotificationAudioState(true);
            }
            toast.success('✅ OnCall audio alerts enabled.', { position: 'top-right', autoClose: 4000 });
        } catch (error) {
            console.error('Unable to enable OnCall audio alerts:', error);
            toast.error('❌ Unable to enable audio alerts in this browser session.', { position: 'top-right', autoClose: 4000 });
        }
    }

    function muteAudioAlerts() {
        if (replayTimerRef.current) {
            clearTimeout(replayTimerRef.current);
            replayTimerRef.current = null;
        }

        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }

        setPlayNotificationAudioState(false);
        setAudioEnabledState(false);
        localStorage.setItem('oncallAudioEnabled', 'false');
    }

    function audioAlertButtonClicked() {
        if (audioEnabledState) {
            muteAudioAlerts();
            return;
        }

        enableAudioAlerts();
    }


    function newCallButtonClicked() {
        if (modalActiveState) {
            setModalActiveState(false);
        } else {
            setModalActiveState(true);
        }
    }

    function modalClose() {
        var params = {};
        apiCall(params);
        setModalActiveState(false);
    };

    if (!isLoadedState) {
        return (
            <div className='main_div'>
                <LoadingComp />
            </div>
        )
    } else if (apiErrorState) {
        return (
            <ApiError />
        )
    } else {
        return (
            <div className='main_div'>
                <div className='internal_main_div'>
                    <Header
                        title='OnCall'
                        rightContent={<>
                            {allowManageCalls && (
                                <button
                                    type='button'
                                    className={`module-page-header__button oncall_enable_audio_button${audioEnabledState ? ' oncall_enable_audio_button_muted' : ''}`}
                                    onClick={audioAlertButtonClicked}
                                >
                                    {audioEnabledState ? <FaVolumeMute className='oncall_audio_button_icon' aria-hidden='true' /> : <FaVolumeUp className='oncall_audio_button_icon' aria-hidden='true' />}
                                    {audioEnabledState ? 'Mute Audio' : 'Enable Audio Alerts'}
                                </button>
                            )}
                            <button type='button' className='module-page-header__button module-page-header__button--icon' onClick={newCallButtonClicked} aria-label='Create new OnCall request' title='Create new OnCall request'>
                                <MdNotificationAdd aria-hidden='true' />
                            </button>
                        </>}
                    >
                            {!allowManageCalls && (
                                <div className='oncall_current_rota_card'>
                                    <span className='oncall_current_rota_label'>Current On Call</span>
                                    <strong className={`oncall_current_rota_value${onCallCurrentOverrideState ? ' oncall_current_rota_value--override' : ''}`}>{onCallRotaDisplayState}</strong>
                                </div>
                            )}
                    </Header>
                    <OnCallAddNewCallFormModal modalActiveState={modalActiveState} setStateFunction={setModalActiveState} onModalClose={modalClose} />
                    <div className='oncall_calls_main_div'>
                        {shouldShowOnCallDayRota && (
                            <div className='oncall_calls_pending_calls_outer_div oncall_day_rota_outer_div'>
                                <div className='oncall_day_rota_header_row'>
                                    <h1 className='onCallState_header'>Today&apos;s On Call Rota</h1>
                                    {userCanAddCover(onCallRotaMetaState.masterRotaId) && (
                                        <RotaAddCoverLauncher
                                            rota={{
                                                masterRotaId: onCallRotaMetaState.masterRotaId,
                                                rotaName: onCallRotaMetaState.rotaName || "Today's On Call Rota",
                                            }}
                                            triggerClassName='oncall_add_cover_button'
                                        >
                                            <>
                                                <span className='oncall_add_cover_button_icon'>+</span>
                                                <span className='oncall_add_cover_button_text'>Add Cover</span>
                                            </>
                                        </RotaAddCoverLauncher>
                                    )}
                                </div>
                                <OnCallDayRota blocks={onCallRotaBlocksState} />
                            </div>
                        )}


                        <div className='oncall_calls_pending_calls_outer_div'>
                            <h1 className='onCallState_header'>Pending Calls ({pendingCallCountState})</h1>
                            <div className='oncall_calls_pending_calls_div'>
                                <audio ref={audioRef} id="audio" src={ONCALL_PENDING_AUDIO_URL}></audio>
                                {pendingCallsState.map((item) =>
                                    <OnCallPendingCall
                                        key={item["id"]}
                                        data={item}
                                        activeCallsState={activeCallsState}
                                        setActiveCallsState={setActiveCallsState}
                                        pendingCallsState={pendingCallsState}
                                        setPendingCallsState={setPendingCallsState}
                                        apiCall={apiCall}
                                        onPendingCallAccepted={handlePendingCallAccepted}
                                    />
                                )
                                }
                            </div>
                        </div>


                        <div className='oncall_calls_pending_calls_outer_div'>
                            <h1 className='onCallState_header'>Active Calls ({activeCallCountState})</h1>
                            <div className='oncall_calls_active_calls_div'>

                                {activeCallsState.map((item) =>
                                    <OnCallActiveCall
                                        key={item["id"] + item["isComplete"] + item["assignedUser"] + item["isAcknowledged"] + item["notes"]}
                                        data={item}
                                        slt={dataSltState}
                                        activeCallsState={activeCallsState}
                                        setActiveCallsState={setActiveCallsState}
                                        callTypes={callTypesState}
                                        apiCall={apiCall}
                                    />
                                )
                                }
                            </div>
                        </div>
                    </div>


                </div>
            </div>

        )
    }
};
